import { useEffect, useId, useState, type ReactNode } from "react";
import { useFormik } from "formik";
import { z } from "zod";
import { isEmail } from "@common/helpers/accounts";
import { submitHelper, validateFormikWithZodSchema } from "@helpers/form";
import { ButtonWithLoader } from "@design-system/Button/ButtonWithLoader";
import { FieldError, FieldRoot } from "@design-system/Field/FieldParts";
import { Loader } from "@design-system/Loader/Loader";
import { OTPField } from "@design-system/OTPField/OTPField";
import { useToaster } from "@design-system/ToastMessages/useToaster";
import { VStack } from "@design-system/VStack/VStack";
import { ResendCode } from "./ResendCode";
import type { RequestCodeFn, VerificationCodeFormSchema } from "./types";
import styles from "./CodeVerification.module.scss";

const codeVerificationFormSchema = z.object({
    verificationCode: z.string().regex(/^\d{6}$/, "Type 6-digit code"),
});

export interface CodeVerificationRenderSubmitState {
    isValid: boolean;
    isSubmitting: boolean;
}

export interface CodeVerificationProps {
    phoneOrEmail: string;
    request: RequestCodeFn;
    /** Default true: requests a code on mount and shows a loader until it resolves. */
    requestOnMount?: boolean;
    /** Resolve `false` for a wrong code. Throwing shows a generic error toast. */
    verify?: (code: string) => Promise<boolean>;
    /** Always receives the verified (or, without `verify`, the entered) code. */
    onSuccess: (code: string) => void | Promise<void>;
    lead?: ReactNode;
    resend?: ReactNode;
    renderSubmit?: (state: CodeVerificationRenderSubmitState) => ReactNode;
    submitButtonText?: ReactNode;
}

/**
 * The "enter the code we sent you" step: OTP boxes, resend and submit.
 * Completing the code submits it; a wrong code (verify resolves false) shows
 * an inline error that clears on the next edit.
 */
export const CodeVerification = ({
    phoneOrEmail,
    request,
    requestOnMount = true,
    verify,
    onSuccess,
    submitButtonText = "Confirm",
    resend = <ResendCode request={request} />,
    lead = (
        <p className={styles.lead}>
            We just sent a 6-digit code to your{" "}
            {isEmail(phoneOrEmail) ? "email" : "phone"}. Proceed by typing your
            code below.
        </p>
    ),
    renderSubmit,
}: CodeVerificationProps) => {
    const [isCodeSent, setIsCodeSent] = useState(!requestOnMount);
    const [isWrongCode, setIsWrongCode] = useState(false);
    const wrongCodeMessageId = useId();
    const { error } = useToaster();

    useEffect(() => {
        if (requestOnMount) {
            request().then(() => setIsCodeSent(true));
        }
    }, [request, requestOnMount]);

    const submitCode = async ({
        verificationCode,
    }: VerificationCodeFormSchema) => {
        let isVerified = true;
        if (verify) {
            try {
                isVerified = await verify(verificationCode);
            } catch {
                error("Failed to verify the code");
                return;
            }
        }

        if (!isVerified) {
            setIsWrongCode(true);
            return;
        }

        await onSuccess(verificationCode);
    };

    const formik = useFormik<VerificationCodeFormSchema>({
        initialValues: { verificationCode: "" },
        validate: validateFormikWithZodSchema(codeVerificationFormSchema),
        validateOnMount: true,
        onSubmit: submitHelper({ handler: submitCode }),
    });

    const changeCode = (code: string) => {
        setIsWrongCode(false);
        void formik.setFieldValue("verificationCode", code);
    };

    // Formik keeps state in a ref, so awaiting the set guarantees submitForm reads the full code.
    const submitCompleteCode = async (code: string) => {
        await formik.setFieldValue("verificationCode", code);
        await formik.submitForm();
    };

    if (!isCodeSent) {
        return <Loader />;
    }

    return (
        <VStack gap={16}>
            {lead}
            <form noValidate onSubmit={formik.handleSubmit}>
                <VStack gap={24} align="center">
                    <FieldRoot invalid={isWrongCode}>
                        <OTPField
                            value={formik.values.verificationCode}
                            onValueChange={changeCode}
                            onValueComplete={(code) =>
                                void submitCompleteCode(code)
                            }
                            onBlur={() =>
                                void formik.setFieldTouched(
                                    "verificationCode",
                                    true,
                                )
                            }
                            autoFocus
                            readOnly={formik.isSubmitting}
                            aria-label="Verification code"
                            aria-describedby={
                                isWrongCode ? wrongCodeMessageId : undefined
                            }
                            className={styles.code}
                        />
                        <FieldError
                            id={wrongCodeMessageId}
                            match={isWrongCode}
                            role="alert"
                            className={styles.error}
                        >
                            That code is invalid. Try again.
                        </FieldError>
                    </FieldRoot>
                    <VStack gap={8} align="center">
                        {resend}
                        {renderSubmit ? (
                            renderSubmit({
                                isValid: formik.isValid,
                                isSubmitting: formik.isSubmitting,
                            })
                        ) : (
                            <ButtonWithLoader
                                disabled={!formik.isValid}
                                loading={formik.isSubmitting}
                                size="xl"
                                variant="primary"
                                type="submit"
                            >
                                {submitButtonText}
                            </ButtonWithLoader>
                        )}
                    </VStack>
                </VStack>
            </form>
        </VStack>
    );
};
